import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScenarioEditMessagePopUpMobile.module.css';

interface ScenarioEditMessagePopUpMobileProps {
  onClose: () => void;
  onCopy: () => void;
  onNewChat: () => void;
  onEdit: () => void;
  onRewind?: () => void;
  onRegenerate?: () => void;
  isLatestMessage?: boolean;
  isUserMessage?: boolean;
}

function ScenarioEditMessagePopUpMobile({
  onClose,
  onCopy,
  onNewChat,
  onEdit,
  onRewind,
  onRegenerate,
  isLatestMessage = false,
  isUserMessage = false
}: ScenarioEditMessagePopUpMobileProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Animation effect when component mounts
  useEffect(() => {
    // Small delay to ensure DOM is ready for the animation
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);
    
    // Prevent body scrolling when popup is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
    };
  }, []);

  // Handle backdrop click to close the popup
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle close with animation
  const handleClose = () => {
    setIsVisible(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Handle action buttons
  const handleCopy = () => {
    onCopy();
    handleClose();
  };

  const handleNewChat = () => {
    onNewChat();
    handleClose();
  };

  const handleEdit = () => {
    onEdit();
    handleClose();
  };

  const handleRewind = () => {
    if (onRewind) {
      onRewind();
      handleClose();
    }
  };

  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate();
      handleClose();
    }
  };

  // Create portal content
  const portalContent = (
    <div 
      className={`${styles.backdrop} ${isVisible ? styles.backdropVisible : ''}`}
      onClick={handleBackdropClick}
    >
      <div 
        ref={popupRef}
        className={`${styles.popupContainer} ${isVisible ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.handle}></div>
        
        <button className={styles.actionButton} onClick={handleCopy}>
          <div className={styles.iconContainer}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
            </svg>
          </div>
          <span>Copy</span>
        </button>
        
        <button className={styles.actionButton} onClick={handleNewChat}>
          <div className={styles.iconContainer}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z" fill="currentColor"/>
              <path d="M12 10H16V12H12V16H10V12H6V10H10V6H12V10Z" fill="currentColor"/>
            </svg>
          </div>
          <span>New scenario from here</span>
        </button>
        
        {/* Always show Rewind for user messages and non-latest messages */}
        {(!isLatestMessage || isUserMessage) && (
          <button className={styles.actionButton} onClick={handleRewind}>
            <div className={styles.iconContainer}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V1L7 6L12 11V7C15.31 7 18 9.69 18 13C18 16.31 15.31 19 12 19C8.69 19 6 16.31 6 13H4C4 17.42 7.58 21 12 21C16.42 21 20 17.42 20 13C20 8.58 16.42 5 12 5Z" fill="currentColor"/>
              </svg>
            </div>
            <span>Rewind to here</span>
          </button>
        )}
        
        <button className={styles.actionButton} onClick={handleEdit}>
          <div className={styles.iconContainer}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
            </svg>
          </div>
          <span>Edit message</span>
        </button>
        
        {/* Show Regenerate only for latest non-user message */}
        {isLatestMessage && !isUserMessage && (
          <button className={styles.actionButton} onClick={handleRegenerate}>
            <div className={styles.iconContainer}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor"/>
              </svg>
            </div>
            <span>Regenerate message</span>
          </button>
        )}
      </div>
    </div>
  );

  // Use createPortal to render the popup at the end of the document body
  return createPortal(portalContent, document.body);
}

export default ScenarioEditMessagePopUpMobile; 